import { bitcoinChain } from '@package/smart-wallet/constants';
import { useMagic } from '@package/smart-wallet/providers';
import { useChainStore } from '@package/smart-wallet/store';
import { useQuery } from '@tanstack/react-query';

interface MempoolAddressResponse {
  address: string;
  chain_stats: {
    funded_txo_count: number;
    funded_txo_sum: number;
    spent_txo_count: number;
    spent_txo_sum: number;
    tx_count: number;
  };
  mempool_stats: {
    funded_txo_count: number;
    funded_txo_sum: number;
    spent_txo_count: number;
    spent_txo_sum: number;
    tx_count: number;
  };
}

export const useGetBitcoinBalance = (address?: string | null) => {
  const { bitcoin } = useMagic();
  const chainId = useChainStore((state) => state.chainId);

  return useQuery({
    queryKey: ['bitcoin-balance', address],
    queryFn: async () => {
      if (!address) {
        throw new Error('Bitcoin address is required');
      }

      // Use testnet or mainnet based on environment
      const baseUrl =
        import.meta.env.VITE_ENV === 'production'
          ? 'https://mempool.space/api'
          : 'https://mempool.space/testnet/api';

      const url = `${baseUrl}/address/${address}`;

      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Mempool API error: ${response.status}`);
        }

        const data: MempoolAddressResponse = await response.json();

        // Calculate balance: (received - spent) + mempool pending
        const chainBalance =
          data.chain_stats.funded_txo_sum - data.chain_stats.spent_txo_sum;
        const mempoolBalance =
          data.mempool_stats.funded_txo_sum - data.mempool_stats.spent_txo_sum;
        const balanceInSatoshis = chainBalance + mempoolBalance;

        // Return balance as string of satoshis (integer)
        // This is compatible with formatUnitsToAmount and ethers.js
        return balanceInSatoshis.toString();
      } catch (error) {
        console.error('Error fetching Bitcoin balance:', error);
        throw error;
      }
    },
    retry: 3,
    enabled: !!address && !!bitcoin && chainId === bitcoinChain.id,
    staleTime: 10000, // 10 seconds
    refetchInterval: 20000, // Refetch every 20 seconds
  });
};
